import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, ArrowRight, Trash2, History } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { listMyContracts, deleteContract, type ContractRow } from "@/lib/contracts.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/clause-history")({
  head: () => ({
    meta: [
      { title: "Clause History — legaldecoder" },
      {
        name: "description",
        content:
          "Track changes across versions of your contracts. See exactly what was added, removed, or rewritten between drafts.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ClauseHistoryPage,
});

function ClauseHistoryPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<ContractRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const list = useServerFn(listMyContracts);
  const del = useServerFn(deleteContract);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate({ to: "/auth", search: { redirect: "/clause-history" } as never });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    list()
      .then((rows) => setContracts(rows))
      .catch((e) => {
        console.error(e);
        toast.error("Couldn't load your contracts.");
      })
      .finally(() => setLoading(false));
  }, [user, list]);

  const handleDelete = async (contractId: string, label: string) => {
    if (!confirm(`Delete "${label}" and all its versions? This cannot be undone.`)) return;
    try {
      await del({ data: { contractId } });
      setContracts((cur) => (cur ? cur.filter((c) => c.id !== contractId) : cur));
      toast.success("Contract deleted.");
    } catch {
      toast.error("Couldn't delete that contract.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <History className="h-6 w-6 text-primary-glow" />
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Clause History</h1>
            </div>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Track changes across versions of the same contract. We show what was added, removed,
              or rewritten between drafts so you sign with confidence.
            </p>
          </div>
        </div>

        {loading || authLoading ? (
          <div className="flex items-center justify-center rounded-2xl border border-border bg-card py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !contracts || contracts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-4 text-lg font-bold">No tracked contracts yet</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Analyze a contract, then click <strong>Save as version</strong> on the results page to
              start tracking changes between drafts.
            </p>
            <Button variant="hero" className="mt-6" asChild>
              <Link to="/">Analyze a contract</Link>
            </Button>
          </div>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {contracts.map((c) => (
              <li
                key={c.id}
                className="group rounded-2xl border border-border bg-card p-5 shadow-card transition-colors hover:border-primary-glow/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-bold">{c.label}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {c.version_count} version{c.version_count === 1 ? "" : "s"} · updated{" "}
                      {new Date(c.updated_at).toLocaleDateString()}
                    </div>
                    {c.tool_slug && (
                      <span className="mt-2 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {c.tool_slug.replace(/-/g, " ")}
                      </span>
                    )}
                    {c.latest_risk && (
                      <span
                        className={
                          "ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                          (c.latest_risk === "high"
                            ? "bg-destructive/15 text-destructive"
                            : c.latest_risk === "medium"
                              ? "bg-warning/15 text-warning"
                              : "bg-success/15 text-success")
                        }
                      >
                        Latest: {c.latest_risk}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(c.id, c.label)}
                    className="rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                    aria-label="Delete contract"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Button variant="outline" size="sm" className="mt-4 w-full gap-2" asChild>
                  <Link to="/clause-history/$contractId" params={{ contractId: c.id }}>
                    Open versions <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-12 text-center text-xs uppercase tracking-wider text-muted-foreground">
          Not legal advice. For informational purposes only.
        </p>
      </main>
      <Footer />
    </div>
  );
}
